<script setup lang="ts">
import { type Ref, computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { EmblaCarouselType } from 'embla-carousel'
import type { ICarousel } from './types'

const emblaApi: Ref<EmblaCarouselType> | undefined = inject('emblaApi')
const carouselProps: ICarousel | undefined = inject('carouselProps')
const mobileMediaQuery = ref<MediaQueryList | null>(null)

// State related to the scrollbar
const scrollbarWidth = ref(0)
const scrollbarPosition = ref(0)
const isDragging = ref(false)
const startX = ref(0)
const initialScrollbarPosition = ref(0)
const isScrollbarVisible = ref(false)

const scrollbarRef = ref<HTMLElement | null>(null)

// Variables to store original scroll duration and friction
let originalScrollDuration: number
let originalScrollFriction: number

// Helper function to check if the slides overflow the container width
function areSlidesOverflowing(): boolean {
    if (!emblaApi?.value) {
        return false
    }

    const slides = emblaApi.value.slideNodes()
    const container = emblaApi.value.containerNode()
    const containerWidth = container.getBoundingClientRect().width

    const totalSlidesWidth = slides.reduce((sum, slide) => {
        return sum + slide.getBoundingClientRect().width
    }, 0)

    return totalSlidesWidth > containerWidth
}

/**
 * Updates the scrollbar's width and position based on the carousel's progress.
 * The width of the scrollbar thumb reflects the portion of the slides visible in the carousel.
 * The position of the scrollbar thumb corresponds to the scroll progress of the carousel.
 */
function updateScrollbar(): void {
    if (!emblaApi?.value) {
        return
    }

    const totalSlides = emblaApi.value.slideNodes().length

    const containerWidth = emblaApi.value.rootNode().clientWidth
    const slideWidth = emblaApi.value.slideNodes()[0]?.clientWidth || containerWidth

    const visibleSlides = containerWidth / slideWidth
    const progress = emblaApi.value.scrollProgress()
    const thumbWidth = (visibleSlides / totalSlides) * 100

    scrollbarWidth.value = thumbWidth

    // Update scrollbar position only when not dragging
    if (!isDragging.value) {
        scrollbarPosition.value = progress * (100 - thumbWidth)
    }
}

function setScrollbarVisible(): void {
    isScrollbarVisible.value = areSlidesOverflowing()
}

function initScrollbar(): void {
    setScrollbarVisible()
    updateScrollbar()
}

/**
 * Initializes event listeners on the carousel for 'scroll', 'resize', and 'slidesChanged'.
 * These events ensure that the scrollbar is kept up-to-date with the carousel's state.
 */
function setupEmbla(): void {
    if (emblaApi?.value) {
        emblaApi.value.on('scroll', updateScrollbar)
        emblaApi.value.on('resize', initScrollbar)
        emblaApi.value.on('slidesChanged', initScrollbar)
        emblaApi.value.on('reInit', initScrollbar)

        initScrollbar()
    }
}

/**
 * Starts the drag process when the scrollbar thumb is pressed.
 * The drag can be initiated via mouse (on desktop) or touch (on mobile).
 */
function startDrag(event: MouseEvent | TouchEvent): void {
    if (!emblaApi?.value || !scrollbarRef.value || scrollbarWidth.value >= 100) {
        return
    }

    isDragging.value = true
    startX.value = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX
    initialScrollbarPosition.value = scrollbarPosition.value

    const engine = emblaApi.value.internalEngine()

    // Save original scroll duration and friction
    originalScrollDuration = engine.scrollBody.duration()
    originalScrollFriction = engine.scrollBody.useFriction(1).duration()

    // Set new values to eliminate inertia and animations
    engine.scrollBody.useDuration(0)
    engine.scrollBody.useFriction(0.8)

    // Add listeners for mouse or touch move and end events
    document.addEventListener('mousemove', onDrag)
    document.addEventListener('mouseup', stopDrag)

    document.addEventListener('touchmove', onDrag)
    document.addEventListener('touchend', stopDrag)

    event.preventDefault()
}

/**
 * Handles the dragging of the scrollbar thumb.
 * This function adjusts the carousel's position in sync with the scrollbar thumb's movement.
 */
function onDrag(event: MouseEvent | TouchEvent): void {
    if (!isDragging.value || !scrollbarRef.value || !emblaApi?.value) {
        return
    }

    const deltaX = event instanceof MouseEvent ? event.clientX - startX.value : event.touches[0].clientX - startX.value

    const trackWidth = scrollbarRef.value.offsetWidth
    const maxScrollbarPosition = 100 - scrollbarWidth.value

    if (maxScrollbarPosition === 0) {
        return
    }

    // Calculate new scrollbar position and ensure it's within bounds
    let newScrollbarPosition = initialScrollbarPosition.value + (deltaX / trackWidth) * 100
    newScrollbarPosition = Math.max(0, Math.min(newScrollbarPosition, maxScrollbarPosition))

    scrollbarPosition.value = newScrollbarPosition

    const newProgress = 1 - newScrollbarPosition / maxScrollbarPosition
    const engine = emblaApi.value.internalEngine()

    // Stop any ongoing animations and reset the target position
    engine.animation.stop()
    engine.target.set(engine.location.get())

    // Calculate the target scroll position in the carousel based on the new scrollbar position
    const targetScrollPosition = engine.limit.min + newProgress * (engine.limit.max - engine.limit.min)
    engine.scrollTo.distance(targetScrollPosition - engine.target.get(), false)
}

/**
 * Stops the drag process when the mouse or touch is released.
 * Restores the original scroll duration and friction settings.
 */
function stopDrag(): void {
    isDragging.value = false
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDrag)
    document.removeEventListener('touchmove', onDrag)
    document.removeEventListener('touchend', stopDrag)

    if (!emblaApi?.value) {
        return
    }

    const engine = emblaApi.value.internalEngine()

    // Restore original scroll duration and friction settings
    engine.scrollBody.useDuration(originalScrollDuration)
    engine.scrollBody.useFriction(originalScrollFriction)

    // Update the scrollbar after dragging
    updateScrollbar()
}

/**
 * Handles clicks on the scrollbar track, allowing users to click directly on the track
 * to move the scrollbar thumb to the clicked position, which scrolls the carousel accordingly.
 */
function onTrackClick(event: MouseEvent): void {
    if (!emblaApi?.value || !scrollbarRef.value || scrollbarWidth.value >= 100) {
        return
    }

    const trackRect = scrollbarRef.value.getBoundingClientRect()
    const clickPosition = event.clientX - trackRect.left
    const trackWidth = trackRect.width
    const maxScrollbarPosition = 100 - scrollbarWidth.value

    // Calculate new scrollbar position in percentage
    let newScrollbarPosition = (clickPosition / trackWidth) * 100 - scrollbarWidth.value / 2

    // Ensure the position is within 0 - maxScrollbarPosition
    newScrollbarPosition = Math.max(0, Math.min(newScrollbarPosition, maxScrollbarPosition))

    scrollbarPosition.value = newScrollbarPosition

    // Invert newProgress to maintain consistency with onDrag function
    const newProgress = 1 - newScrollbarPosition / maxScrollbarPosition

    const engine = emblaApi.value.internalEngine()

    // Save original scroll duration and friction
    const originalScrollDuration = engine.scrollBody.duration()
    const originalScrollFriction = engine.scrollBody.useFriction(1).duration()

    // Set new values to eliminate inertia and animations
    engine.scrollBody.useDuration(0)
    engine.scrollBody.useFriction(0.8)

    // Stop any ongoing animations
    engine.animation.stop()

    // Reset target to avoid scrolling errors
    engine.target.set(engine.location.get())

    // Calculate the target scroll position
    const targetScrollPosition = engine.limit.min + newProgress * (engine.limit.max - engine.limit.min)

    // Scroll the carousel to the new position
    engine.scrollTo.distance(targetScrollPosition - engine.target.get(), false)

    // Restore original scroll duration and friction
    engine.scrollBody.useDuration(originalScrollDuration)
    engine.scrollBody.useFriction(originalScrollFriction)

    // Update the scrollbar after clicking
    updateScrollbar()
}

// Watch for changes in props.embla and setup the carousel listeners when embla instance is available
watch(
    () => emblaApi?.value,
    newEmbla => {
        if (newEmbla) {
            setupEmbla()
        }
    },
    { immediate: true },
)

function handleMediaQueryChange(): void {
    setScrollbarVisible()
    updateScrollbar()
}

onMounted(() => {
    mobileMediaQuery.value = window.matchMedia('(max-width: 1024px)')

    mobileMediaQuery.value.addEventListener('change', handleMediaQueryChange)
})

onBeforeUnmount(() => {
    mobileMediaQuery.value?.removeEventListener('change', handleMediaQueryChange)
})

const scrollbarVisibleClasses = computed(() =>
    isScrollbarVisible.value
        ? [
              'visible',
              carouselProps?.disableMobile && 'max-md:invisible',
              carouselProps?.disableTablet && 'md:max-lg:invisible',
              carouselProps?.disableDesktop && 'lg:invisible',
          ]
        : 'invisible',
)
</script>

<template>
    <div
        ref="scrollbarRef"
        class="group relative mx-auto mt-2 h-6 w-1/3 cursor-pointer overflow-hidden rounded-full lg:mt-4"
        :class="scrollbarVisibleClasses"
        @click="onTrackClick"
    >
        <!-- Scrollbar track -->
        <div
            class="bg-neutral-tertiary rounded-button absolute bottom-3 h-1 w-full translate-y-1/2 group-hover:h-1.5"
        />
        <!-- Scrollbar thumb -->
        <div
            class="absolute h-full cursor-grab"
            :class="isDragging && 'cursor-grabbing'"
            :style="{ width: `${scrollbarWidth}%`, left: `${scrollbarPosition}%` }"
            @mousedown="startDrag"
            @touchstart="startDrag"
            @click.stop
        >
            <div
                class="bg-neutral-primary-invert rounded-button absolute bottom-3 h-1 w-full translate-y-1/2 group-hover:h-1.5"
            />
        </div>
    </div>
</template>
